import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  Fade,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonIcon from '@mui/icons-material/Person';
import UpdateIcon from '@mui/icons-material/Update';
import pptPoliciesService from '../../services/pptPoliciesService';
import { API_BASE_URL } from '../../config';
import { IPptPolicyContent, IPptPolicyDownload, IPptPolicyExample, IPptPolicyViewResponse } from '../../types/pptPolicies';

const COLORS = {
  olympia: '#1B3967',
  olympiaLight: '#2a5087',
  originalet: '#0050E6',
  sandLight: '#F5F0E8',
  sand: '#EDE8DC',
  sandDark: '#D4CFC3',
  white: '#FFFFFF',
  text: '#2d3748',
  textLight: '#718096',
  success: '#2e7d32',
  successBg: 'rgba(46,125,50,0.06)',
  successBorder: 'rgba(46,125,50,0.2)',
  error: '#c62828',
  errorBg: 'rgba(198,40,40,0.05)',
  errorBorder: 'rgba(198,40,40,0.18)',
  cardBorder: 'rgba(0,0,0,0.06)',
  sectionDivider: 'rgba(0,0,0,0.06)',
  heroGradient: 'linear-gradient(135deg, #1B3967 0%, #0d2847 40%, #0a1f3a 100%)',
};

interface PptPoliciesPageProps {
  onClose: () => void;
  canEdit?: boolean;
  onOpenAdmin?: () => void;
  onOpenBrandGuide?: () => void;
}

const resolveAssetUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE_URL}${url}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Ej publicerad';
  try {
    return new Date(value).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

const getFileTypeIcon = (fileType: string) => {
  const lower = (fileType || '').toLowerCase();
  if (lower === 'pdf') return <PictureAsPdfIcon sx={{ fontSize: 20 }} />;
  if (['ppt', 'pptx', 'potx'].includes(lower)) return <SlideshowIcon sx={{ fontSize: 20 }} />;
  return <InsertDriveFileIcon sx={{ fontSize: 20 }} />;
};

const getFileTypeColor = (fileType: string): string => {
  const lower = (fileType || '').toLowerCase();
  if (lower === 'pdf') return '#d32f2f';
  if (['ppt', 'pptx', 'potx'].includes(lower)) return '#d35230';
  return '#546e7a';
};

const hasPreview = (download: IPptPolicyDownload) =>
  Boolean(download.previewUrl || download.fileType.toLowerCase() === 'pdf');

const smoothScrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const EmptyState = ({ canEdit, onOpenAdmin }: { canEdit: boolean; onOpenAdmin?: () => void }) => (
  <Paper
    sx={{
      p: { xs: 4, md: 6 },
      borderRadius: 4,
      textAlign: 'center',
      border: `2px dashed ${COLORS.sandDark}`,
      bgcolor: COLORS.sandLight,
    }}
  >
    <SlideshowIcon sx={{ fontSize: 56, color: COLORS.olympia, opacity: 0.5, mb: 2 }} />
    <Typography variant="h5" sx={{ mb: 1, color: COLORS.olympia, fontWeight: 700 }}>
      Sidan är inte publicerad ännu
    </Typography>
    <Typography sx={{ color: COLORS.textLight, mb: canEdit && onOpenAdmin ? 4 : 0, maxWidth: 440, mx: 'auto', lineHeight: 1.7 }}>
      När innehåll har skapats och publicerats av en administratör visas PPT Policys här.
    </Typography>
    {canEdit && onOpenAdmin && (
      <Button
        variant="contained"
        size="large"
        startIcon={<SettingsIcon />}
        onClick={onOpenAdmin}
        sx={{
          bgcolor: COLORS.olympia,
          px: 4,
          py: 1.5,
          borderRadius: 2.5,
          fontWeight: 700,
          textTransform: 'none',
          fontSize: '1rem',
          '&:hover': { bgcolor: COLORS.olympiaLight },
        }}
      >
        Öppna admin
      </Button>
    )}
  </Paper>
);

const SectionNumber = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      bgcolor: COLORS.olympia,
      color: COLORS.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 800,
      fontSize: '0.85rem',
      flexShrink: 0,
    }}
  >
    {number}
  </Box>
);

const PptPoliciesPage: React.FC<PptPoliciesPageProps> = ({
  onClose,
  canEdit = false,
  onOpenAdmin,
  onOpenBrandGuide,
}) => {
  const [response, setResponse] = useState<IPptPolicyViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    pptPoliciesService
      .fetchPage()
      .then((data) => {
        if (!mounted) return;
        setResponse(data);
        setError(null);
      })
      .catch((err: any) => {
        if (!mounted) return;
        if (err?.response?.status === 404) {
          setResponse(null);
          setError(null);
          return;
        }
        setError(err?.response?.data?.message || err.message || 'Kunde inte hämta PPT Policys.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  const content: IPptPolicyContent | null = response?.content || null;

  const sections = useMemo(
    () => [...(content?.sections || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.title.localeCompare(b.title)),
    [content]
  );

  const doExamples = useMemo(
    () => [...(content?.examples || [])].filter((e) => e.type === 'do').sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [content]
  );

  const dontExamples = useMemo(
    () => [...(content?.examples || [])].filter((e) => e.type === 'dont').sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [content]
  );

  const downloads = useMemo(
    () => [...(content?.downloads || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.title.localeCompare(b.title)),
    [content]
  );

  const hasDownloads = downloads.length > 0;
  const hasChecklist = (content?.quickChecklist?.length ?? 0) > 0;

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: COLORS.olympia }} size={48} />
        <Typography sx={{ color: COLORS.textLight }}>Laddar PPT Policys...</Typography>
      </Box>
    );
  }

  const renderExampleCard = (example: IPptPolicyExample) => {
    const isDo = example.type === 'do';
    const lines = (example.description || '').split('\n').map((l) => l.trim()).filter(Boolean);
    const bulletColor = isDo ? COLORS.success : COLORS.error;

    return (
      <Box
        key={example._id || `${example.title}-${example.displayOrder}`}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: `1.5px solid ${isDo ? COLORS.successBorder : COLORS.errorBorder}`,
          backgroundColor: isDo ? COLORS.successBg : COLORS.errorBg,
          transition: 'box-shadow 0.2s, transform 0.15s',
          '&:hover': {
            boxShadow: `0 4px 20px ${isDo ? 'rgba(46,125,50,0.12)' : 'rgba(198,40,40,0.1)'}`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: bulletColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isDo ? (
              <CheckCircleOutlineIcon sx={{ color: COLORS.white, fontSize: 18 }} />
            ) : (
              <HighlightOffIcon sx={{ color: COLORS.white, fontSize: 18 }} />
            )}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.text }}>
            {example.title}
          </Typography>
        </Stack>

        {/* Each line as a bullet row */}
        <Stack spacing={0.75}>
          {lines.map((line, lineIndex) => (
            <Box
              key={lineIndex}
              sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: 'flex-start',
                py: 0.5,
                pl: 0.5,
              }}
            >
              {isDo ? (
                <CheckCircleOutlineIcon sx={{ color: bulletColor, fontSize: 19, mt: '2px', flexShrink: 0 }} />
              ) : (
                <HighlightOffIcon sx={{ color: bulletColor, fontSize: 19, mt: '2px', flexShrink: 0 }} />
              )}
              <Typography sx={{ color: COLORS.text, lineHeight: 1.6, fontSize: '0.92rem' }}>
                {line}
              </Typography>
            </Box>
          ))}
        </Stack>

        {example.imageUrl && (
          <Box
            component="img"
            src={resolveAssetUrl(example.imageUrl)}
            alt={example.title}
            sx={{
              mt: 2.5,
              width: '100%',
              maxHeight: 240,
              borderRadius: 2.5,
              objectFit: 'cover',
              border: `1px solid ${isDo ? COLORS.successBorder : COLORS.errorBorder}`,
            }}
          />
        )}
      </Box>
    );
  };

  const renderChecklist = () => {
    if (!content || !hasChecklist) return null;
    return (
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
          <ListAltIcon sx={{ color: COLORS.originalet, fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: COLORS.olympia, fontWeight: 800, fontSize: '1.1rem' }}>
            Snabbguide
          </Typography>
        </Stack>
        <Stack spacing={0}>
          {content.quickChecklist
            .slice()
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map((item, index) => (
              <Box
                key={item._id || `${item.text}-${item.displayOrder}`}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  py: 1.25,
                  px: 1.5,
                  borderRadius: 1.5,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'rgba(0,80,230,0.04)' },
                  borderBottom: index < content.quickChecklist.length - 1 ? `1px solid ${COLORS.cardBorder}` : 'none',
                }}
              >
                <CheckCircleOutlineIcon sx={{ color: COLORS.originalet, mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                <Typography sx={{ lineHeight: 1.65, color: COLORS.text, fontSize: '0.9rem' }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100%', bgcolor: '#f7f8fa', position: 'relative' }}>
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 20,
          bgcolor: COLORS.white,
          boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
          '&:hover': { bgcolor: COLORS.sandLight },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Back to top */}
      <Fade in={showBackToTop}>
        <Fab
          size="small"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 20,
            bgcolor: COLORS.olympia,
            color: COLORS.white,
            '&:hover': { bgcolor: COLORS.olympiaLight },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Fade>

      {/* Hero */}
      <Box
        sx={{
          background: COLORS.heroGradient,
          color: COLORS.white,
          px: { xs: 3, md: 6 },
          py: { xs: 7, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: -120, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: '20%', width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.02)' }} />

        <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: 4, opacity: 0.6, fontSize: '0.7rem', mb: 1.5, display: 'block' }}
          >
            POWERPOINT RIKTLINJER
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.4rem', md: '3.6rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              mb: 2.5,
            }}
          >
            {content?.title || 'PPT Policys'}
          </Typography>
          <Typography
            sx={{
              maxWidth: 720,
              fontSize: { xs: '1.05rem', md: '1.2rem' },
              lineHeight: 1.75,
              opacity: 0.88,
              mb: 4,
            }}
          >
            {content?.intro || 'Här samlar vi riktlinjer, exempel och mallar för PowerPoint-presentationer.'}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4, flexWrap: 'wrap' }}>
            {response?.lastPublishedAt && (
              <Chip
                icon={<UpdateIcon sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
                label={formatDate(response.lastPublishedAt)}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: COLORS.white, backdropFilter: 'blur(4px)' }}
              />
            )}
            {(content?.ownerName || content?.ownerTeam) && (
              <Chip
                icon={<PersonIcon sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
                label={[content?.ownerName, content?.ownerTeam].filter(Boolean).join(' · ')}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: COLORS.white, backdropFilter: 'blur(4px)' }}
              />
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {hasDownloads && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => smoothScrollTo('ppt-downloads')}
                sx={{
                  bgcolor: COLORS.white,
                  color: COLORS.olympia,
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.3,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: COLORS.sandLight, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' },
                }}
              >
                Ladda ner mallar
              </Button>
            )}
            {onOpenBrandGuide && (
              <Button
                variant="contained"
                startIcon={<AutoStoriesIcon />}
                onClick={onOpenBrandGuide}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: COLORS.white,
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.3,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  backdropFilter: 'blur(4px)',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
                }}
              >
                Grafisk manual
              </Button>
            )}
            {canEdit && onOpenAdmin && (
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={onOpenAdmin}
                sx={{
                  borderColor: 'rgba(255,255,255,0.35)',
                  color: COLORS.white,
                  fontWeight: 600,
                  px: 3,
                  py: 1.3,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': { borderColor: COLORS.white, bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Hantera sida
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
        )}

        {!content && !error && <EmptyState canEdit={canEdit} onOpenAdmin={onOpenAdmin} />}

        {response?.isDraftPreview && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            Du ser ett utkast eftersom sidan inte är publicerad ännu.
          </Alert>
        )}

        {content && (
          <Stack spacing={5}>
            {/* Policy + Checklist: two-column layout */}
            {sections.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 3, lg: 4 },
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'flex-start',
                }}
              >
                {/* Left: single white block with all sections */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 4,
                    border: `1px solid ${COLORS.cardBorder}`,
                    overflow: 'hidden',
                  }}
                >
                  {/* TOC at top of the block */}
                  {content.tocEnabled && sections.length > 1 && (
                    <Box sx={{ px: { xs: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
                        Innehåll
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {sections.map((section, index) => (
                          <Chip
                            key={section._id || section.anchor}
                            label={`${index + 1}. ${section.title}`}
                            size="small"
                            onClick={() => smoothScrollTo(section.anchor)}
                            sx={{
                              cursor: 'pointer',
                              fontWeight: 500,
                              fontSize: '0.82rem',
                              height: 30,
                              bgcolor: COLORS.sandLight,
                              color: COLORS.olympia,
                              '&:hover': { bgcolor: COLORS.sand },
                            }}
                          />
                        ))}
                      </Stack>
                      <Divider sx={{ mt: 2.5, borderColor: COLORS.sectionDivider }} />
                    </Box>
                  )}

                  {/* All sections inside one block */}
                  {sections.map((section, index) => (
                    <Box
                      key={section._id || section.anchor}
                      id={section.anchor}
                      sx={{
                        px: { xs: 3, md: 5 },
                        py: { xs: 3, md: 4 },
                        scrollMarginTop: 80,
                        borderTop: index > 0 || (content.tocEnabled && sections.length > 1) ? `1px solid ${COLORS.sectionDivider}` : 'none',
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
                        <SectionNumber number={index + 1} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h4" sx={{ color: COLORS.olympia, fontWeight: 800, lineHeight: 1.2 }}>
                            {section.title}
                          </Typography>
                          {section.introText && (
                            <Typography
                              sx={{
                                color: COLORS.textLight,
                                fontSize: '1.05rem',
                                lineHeight: 1.75,
                                mt: 1.5,
                                whiteSpace: 'pre-line',
                              }}
                            >
                              {section.introText}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {section.content && (
                        <Typography sx={{ color: COLORS.text, lineHeight: 1.85, whiteSpace: 'pre-line', fontSize: '0.98rem', pl: { md: 6.5 } }}>
                          {section.content}
                        </Typography>
                      )}

                      {section.images.length > 0 && (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ mt: 3, pl: { md: 6.5 }, flexWrap: 'wrap' }}>
                          {section.images
                            .slice()
                            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                            .map((image) => (
                              <Box key={image._id || `${image.url}-${image.displayOrder}`} sx={{ flex: '1 1 280px', minWidth: 0 }}>
                                <Box
                                  component="img"
                                  src={resolveAssetUrl(image.url)}
                                  alt={image.alt || section.title}
                                  sx={{
                                    width: '100%',
                                    maxHeight: 320,
                                    objectFit: 'cover',
                                    borderRadius: 2.5,
                                    border: `1px solid ${COLORS.cardBorder}`,
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.01)' },
                                  }}
                                />
                                {(image.alt || image.caption) && (
                                  <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: COLORS.textLight }}>
                                    {image.caption || image.alt}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Paper>

                {/* Right: sticky checklist sidebar */}
                {hasChecklist && (
                  <Paper
                    elevation={0}
                    sx={{
                      width: { xs: '100%', lg: 320 },
                      flexShrink: 0,
                      borderRadius: 4,
                      border: `1px solid ${COLORS.cardBorder}`,
                      p: { xs: 2.5, md: 3 },
                      position: { lg: 'sticky' },
                      top: { lg: 24 },
                      alignSelf: { lg: 'flex-start' },
                    }}
                  >
                    {renderChecklist()}
                  </Paper>
                )}
              </Box>
            )}

            {/* Checklist standalone if no sections exist */}
            {sections.length === 0 && hasChecklist && (
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: `1px solid ${COLORS.cardBorder}` }}>
                {renderChecklist()}
              </Paper>
            )}

            {/* Dos and Don'ts — side by side */}
            {(doExamples.length > 0 || dontExamples.length > 0) && (
              <Box>
                <Typography variant="h4" sx={{ color: COLORS.olympia, fontWeight: 800, mb: 1.5 }}>
                  Dos &amp; Don&apos;ts
                </Typography>
                <Typography sx={{ color: COLORS.textLight, mb: 4, fontSize: '1.05rem', lineHeight: 1.7 }}>
                  Exempel på bra och mindre bra lösningar i presentationer.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    alignItems: 'start',
                  }}
                >
                  {/* Do column */}
                  {doExamples.length > 0 && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <CheckCircleOutlineIcon sx={{ color: COLORS.success }} />
                        <Typography variant="h6" sx={{ color: COLORS.success, fontWeight: 700, letterSpacing: '0.3px' }}>
                          Gör så här
                        </Typography>
                      </Stack>
                      <Stack spacing={2}>
                        {doExamples.map(renderExampleCard)}
                      </Stack>
                    </Box>
                  )}

                  {/* Don't column */}
                  {dontExamples.length > 0 && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <HighlightOffIcon sx={{ color: COLORS.error }} />
                        <Typography variant="h6" sx={{ color: COLORS.error, fontWeight: 700, letterSpacing: '0.3px' }}>
                          Undvik detta
                        </Typography>
                      </Stack>
                      <Stack spacing={2}>
                        {dontExamples.map(renderExampleCard)}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* FAQ */}
            {content.faqItems.length > 0 && (
              <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: `1px solid ${COLORS.cardBorder}` }}>
                <Typography variant="h4" sx={{ color: COLORS.olympia, fontWeight: 800, mb: 1 }}>
                  Vanliga frågor
                </Typography>
                <Typography sx={{ color: COLORS.textLight, mb: 3, lineHeight: 1.7 }}>
                  Svar på de vanligaste frågorna om presentationer och mallar.
                </Typography>
                {content.faqItems
                  .slice()
                  .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                  .map((item, index) => (
                    <Accordion
                      key={item._id || `${item.question}-${item.displayOrder}`}
                      disableGutters
                      elevation={0}
                      sx={{
                        borderBottom: index < content.faqItems.length - 1 ? `1px solid ${COLORS.cardBorder}` : 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { my: 0 },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.olympia }} />}
                        sx={{ px: 0, py: 1 }}
                      >
                        <Typography sx={{ fontWeight: 600, color: COLORS.text, fontSize: '1rem' }}>
                          {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pb: 2.5 }}>
                        <Typography sx={{ color: COLORS.text, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                          {item.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </Paper>
            )}

            {/* Downloads */}
            {hasDownloads && (
              <Paper
                id="ppt-downloads"
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  border: `1px solid ${COLORS.cardBorder}`,
                  scrollMarginTop: 80,
                }}
              >
                <Typography variant="h4" sx={{ color: COLORS.olympia, fontWeight: 800, mb: 1 }}>
                  Mallar och exempel
                </Typography>
                <Typography sx={{ color: COLORS.textLight, mb: 4, lineHeight: 1.7 }}>
                  Ladda ner godkända mallar, stödmaterial och exempelpresentationer.
                </Typography>

                <Stack spacing={2.5}>
                  {downloads.map((download, index) => (
                    <Box
                      key={download._id || `${download.title}-${index}`}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        border: `1px solid ${COLORS.cardBorder}`,
                        transition: 'box-shadow 0.2s, transform 0.15s',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                        <Stack direction="row" spacing={2.5} sx={{ flex: 1 }}>
                          <Tooltip title={download.fileType ? download.fileType.toUpperCase() : 'Fil'}>
                            <Box
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 2.5,
                                bgcolor: `${getFileTypeColor(download.fileType)}14`,
                                border: `1.5px solid ${getFileTypeColor(download.fileType)}30`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: getFileTypeColor(download.fileType),
                              }}
                            >
                              {getFileTypeIcon(download.fileType)}
                            </Box>
                          </Tooltip>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                              {download.fileType && (
                                <Chip size="small" label={download.fileType.toUpperCase()} sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22, bgcolor: `${getFileTypeColor(download.fileType)}14`, color: getFileTypeColor(download.fileType) }} />
                              )}
                              {download.category && <Chip size="small" label={download.category} variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />}
                              {download.isTemplate && <Chip size="small" label="Mall" sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#e3f2fd', color: '#1565c0' }} />}
                              {download.isExample && <Chip size="small" label="Exempel" sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#fce4ec', color: '#c62828' }} />}
                              {download.versionLabel && <Chip size="small" variant="outlined" label={`v${download.versionLabel}`} sx={{ height: 22, fontSize: '0.72rem' }} />}
                            </Stack>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: COLORS.text, mb: 0.5 }}>
                              {download.title}
                            </Typography>
                            {download.description && (
                              <Typography sx={{ color: COLORS.textLight, lineHeight: 1.7, whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                                {download.description}
                              </Typography>
                            )}
                            {download.fileName && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: COLORS.textLight }}>
                                {download.fileName}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Stack direction={{ xs: 'row', md: 'column' }} spacing={1.5} justifyContent="center" sx={{ flexShrink: 0 }}>
                          {hasPreview(download) && (
                            <Button
                              component="a"
                              href={resolveAssetUrl(download.previewUrl || download.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                            >
                              Förhandsvisa
                            </Button>
                          )}
                          <Button
                            component="a"
                            href={resolveAssetUrl(download.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              fontWeight: 600,
                              bgcolor: COLORS.olympia,
                              '&:hover': { bgcolor: COLORS.olympiaLight },
                            }}
                          >
                            Ladda ner
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Footer */}
            <Divider sx={{ borderColor: COLORS.cardBorder }} />
            <Box sx={{ textAlign: 'center', pb: 4 }}>
              {(content.ownerName || content.ownerTeam) && (
                <Typography variant="body2" sx={{ color: COLORS.textLight }}>
                  Innehållsansvarig: {[content.ownerName, content.ownerTeam].filter(Boolean).join(' · ')}
                </Typography>
              )}
              {response?.lastPublishedAt && (
                <Typography variant="caption" sx={{ color: COLORS.textLight, display: 'block', mt: 0.5 }}>
                  Senast uppdaterad {formatDate(response.lastPublishedAt)}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default PptPoliciesPage;
